import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, Mail, Phone, Calendar, UserPlus } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const MyLawyers = ({ userData, setUserData }) => {
    const [activeSubTab, setActiveSubTab] = useState("lawyers");
    const [showAddLawyerModal, setShowAddLawyerModal] = useState(false);
    const [officeData, setOfficeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [newLawyerData, setNewLawyerData] = useState({
        name: "",
        email: "",
        phone: "",
    });

    useEffect(() => {
        const fetchOffice = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                setError("Authentication required.");
                setLoading(false);
                return;
            }

            try {
                const response = await api.get("/offices", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.success && response.data.data) {
                    setOfficeData(response.data.data);
                } else {
                    setError("No office data found.");
                }
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to load office data.";
                setError(msg);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: msg,
                    confirmButtonColor: "#1e40af",
                });
            } finally {
                setLoading(false);
            }
        };

        if (userData.isLawFirm) {
            fetchOffice();
        } else {
            setLoading(false);
        }
    }, [userData.isLawFirm]);

    const handleAddLawyer = async (e) => {
        e.preventDefault();
        setSaving(true);

        const token = localStorage.getItem("auth_token");
        if (!token) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Authentication required.",
                confirmButtonColor: "#1e40af",
            });
            setSaving(false);
            return;
        }

        const payload = {
            name: newLawyerData.name,
            email: newLawyerData.email,
            phone: newLawyerData.phone,
        };

        try {
            const response = await api.post("/offices/members", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data.success) {
                const newMember = response.data.data.member;

                setOfficeData((prev) => ({
                    ...prev,
                    members: [...(prev.members || []), newMember],
                }));

                setShowAddLawyerModal(false);
                setNewLawyerData({
                    name: "",
                    email: "",
                    password: "",
                    password_confirmation: "",
                    phone: "",
                });

                Swal.fire({
                    icon: "success",
                    title: "Lawyer Added!",
                    html: `
            <p class="text-sm">Verification email sent to:</p>
            <p class="font-medium">${newLawyerData.email}</p>
          `,
                    confirmButtonColor: "#1e40af",
                    timer: 4000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to add lawyer.";
            Swal.fire({
                icon: "error",
                title: "Error",
                text: msg,
                confirmButtonColor: "#1e40af",
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <p className="text-red-700 font-medium">{error}</p>
            </div>
        );
    }

    const members = officeData?.members || [];

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Law Firm Dashboard</h2>
                    <p className="text-gray-600 mt-1">Manage your team and office</p>
                </div>
            </div>

            {/* Sub Navigation */}
            <div className="mb-6">
                <nav className="flex space-x-8">
                    {[{ id: "lawyers", label: "Lawyers" }].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeSubTab === tab.id
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Lawyers List */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Firm Lawyers ({members.length})
                    </h3>
                    <motion.button
                        onClick={() => setShowAddLawyerModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <UserPlus className="w-4 h-4" />
                        Add Lawyer
                    </motion.button>
                </div>

                {members.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H9a2 2 0 01-2-2v-1a6 6 0 0112 0v1a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <p className="text-gray-600 font-medium">No lawyers added yet</p>
                        <p className="text-gray-500 text-sm mt-1">Click "Add Lawyer" to invite your team members</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {members.map((member, index) => {
                            const user = member.user;
                            return (
                                <motion.div
                                    key={member.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-sm transition-shadow"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-semibold text-sm">
                                                    {user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-gray-900">{user.name}</h4>
                                                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="w-3.5 h-3.5" />
                                                        {user.email}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Phone className="w-3.5 h-3.5" />
                                                        {user.phone || "—"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs text-gray-500 mb-1">Joined</div>
                                            <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                                                <Calendar className="w-3.5 h-3.5" />
                                                {new Date(member.joined_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add Lawyer Modal */}
            {showAddLawyerModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl p-6 w-full max-w-md"
                    >
                        <h3 className="text-xl font-semibold text-gray-900 mb-5">Invite New Lawyer</h3>
                        <form onSubmit={handleAddLawyer} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                <input
                                    type="text"
                                    value={newLawyerData.name}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="John Smith"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    value={newLawyerData.email}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="lawyer@example.com"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                <input
                                    type="tel"
                                    value={newLawyerData.phone}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, phone: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+1234567890"
                                    required
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddLawyerModal(false)}
                                    disabled={saving}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 disabled:opacity-70`}
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4" />
                                            Send Invitation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default MyLawyers;